import { useCallback, useMemo, useState } from "react";
import type { RgbColor } from "utils/hslToRgb";
import { colorRooms } from "../../CableTakeoffTabV3/colorRooms";
import { roomCircuitColorOf } from "../../CableTakeoffTabV3/RoomCircuitsBuilder";
import {
  buildRoomShapes,
  getWallStrokeWidthPt,
} from "../../utils/floorPlanLayout";
import { useFloorPlanLayout } from "../useFloorPlanLayout";
import { ACTIVE_PAGE_INDEX } from "../useSelectedRoom/constants";
import { FALLBACK_ROOM_COLOR, ROOM_TOUCH_DISTANCE_M } from "./constants";
import type {
  RoomCircuitTint,
  UseCableRoomsOverlay,
  UseCableRoomsOverlayResult,
} from "./types";

const isOnActivePage = (row: { pageIndex: number }) =>
  row.pageIndex === ACTIVE_PAGE_INDEX;

const rgbCss = ([red, green, blue]: RgbColor) => `rgb(${red} ${green} ${blue})`;

export const useCableRoomsOverlay: UseCableRoomsOverlay = ({
  floorPlanId,
  metersPerPdfPoint,
  isEnabled,
}) => {
  const [areRoomsShown, setAreRoomsShown] = useState(true);
  const [roomCircuitTint, setRoomCircuitTint] =
    useState<RoomCircuitTint | null>(null);

  // The preview is worked out on every render; an unchanged tint must not render the page again.
  const tintRooms = useCallback((next: RoomCircuitTint | null) => {
    setRoomCircuitTint((previous) =>
      JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
    );
  }, []);

  // Same query key as execution mode's read, so both share one cached layout.
  const { layout } = useFloorPlanLayout({ floorPlanId, isEnabled });

  const roomLayout = useMemo(
    () => ({
      rooms: layout.rooms.filter(isOnActivePage),
      doors: layout.doors.filter(isOnActivePage),
    }),
    [layout],
  );

  const pageZones = useMemo(
    () => layout.zones.filter(isOnActivePage),
    [layout],
  );

  const neighbourColorById = useMemo(
    () =>
      colorRooms({
        rooms: roomLayout.rooms,
        touchDistance: metersPerPdfPoint
          ? ROOM_TOUCH_DISTANCE_M / metersPerPdfPoint
          : 0,
      }),
    [roomLayout.rooms, metersPerPdfPoint],
  );

  const roomList = useMemo(
    () => ({
      rooms: roomLayout.rooms.map((room) => ({
        id: room.id,
        name: room.name,
        color: rgbCss(neighbourColorById.get(room.id) ?? FALLBACK_ROOM_COLOR),
      })),
      areRoomsShown,
      onToggleShown: setAreRoomsShown,
    }),
    [roomLayout.rooms, neighbourColorById, areRoomsShown],
  );

  // While circuits are being built from rooms, each room takes its proposed circuit's colour and number.
  const roomsOverlay = useMemo(() => {
    const shapes = buildRoomShapes({
      rooms: roomLayout.rooms,
      zones: pageZones,
      roomColorById: new Map(
        roomLayout.rooms.map((room) => {
          const circuitIndex = roomCircuitTint?.[room.id];
          return [
            room.id,
            circuitIndex === undefined
              ? (neighbourColorById.get(room.id) ?? FALLBACK_ROOM_COLOR)
              : roomCircuitColorOf(circuitIndex),
          ];
        }),
      ),
      metersPerPdfPoint,
    }).map((shape) => {
      const circuitIndex =
        roomCircuitTint?.[Number(shape.id.replace(/^room-/, ""))];
      return circuitIndex === undefined || !shape.label
        ? shape
        : {
            ...shape,
            label: {
              ...shape.label,
              text: `${circuitIndex + 1} · ${shape.label.text}`,
            },
          };
    });
    return shapes.length
      ? {
          view: "rooms" as const,
          shapes,
          zoneStrokeWidth: getWallStrokeWidthPt({ metersPerPdfPoint }),
        }
      : null;
  }, [
    roomLayout.rooms,
    pageZones,
    roomCircuitTint,
    neighbourColorById,
    metersPerPdfPoint,
  ]);

  const cableRoomsOverlay: UseCableRoomsOverlayResult = {
    roomLayout,
    roomList,
    overlay: areRoomsShown || roomCircuitTint !== null ? roomsOverlay : null,
    tintRooms,
  };
  return cableRoomsOverlay;
};
